import styled, { keyframes, css } from 'styled-components';
import { Link } from 'react-router-dom';

// ============================================
// DESIGN TOKENS
// ============================================
export const tokens = {
    colors: {
        surface: 'rgba(18, 18, 20, 0.95)',
        surfaceLight: 'rgba(255, 255, 255, 0.05)',
        surfaceLighter: 'rgba(255, 255, 255, 0.08)',
        border: 'rgba(255, 255, 255, 0.1)',
        borderLight: 'rgba(255, 255, 255, 0.15)',
        text: '#ffffff',
        textMuted: 'rgba(255, 255, 255, 0.6)',
        textDim: 'rgba(255, 255, 255, 0.4)',
        accent: '#c80707',
        accentHover: '#e00808',
        accentGlow: 'rgba(200, 7, 7, 0.4)',
        success: '#22c55e',
        successGlow: 'rgba(34, 197, 94, 0.3)',
        gold: '#fbbf24',
        goldGlow: 'rgba(251, 191, 36, 0.3)',
        silver: '#94a3b8',
        silverGlow: 'rgba(148, 163, 184, 0.3)',
        bronze: '#cd7c2f',
        bronzeGlow: 'rgba(205, 124, 47, 0.3)',
    },
    blur: '24px',
    radius: '16px',
    radiusSmall: '10px',
    radiusLarge: '24px',
    shadow: '0 12px 48px rgba(0, 0, 0, 0.5), 0 4px 16px rgba(0, 0, 0, 0.3)',
    shadowHover: '0 20px 60px rgba(0, 0, 0, 0.6), 0 8px 24px rgba(0, 0, 0, 0.4)',
};

// Round colors from constants
export const roundColors = ['#00bc57', '#6e45f9', '#29c2ff', '#f99b45', '#f945a9'];

// ============================================
// ANIMATIONS
// ============================================
export const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

export const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

export const scaleIn = keyframes`
    from {
        opacity: 0;
        transform: scale(0.8);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
`;

export const slideInLeft = keyframes`
    from {
        opacity: 0;
        transform: translateX(-30px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
`;

export const countUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(20px) scale(0.9);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
`;

export const pulseGlow = keyframes`
    0%, 100% {
        box-shadow: 0 0 20px rgba(200, 7, 7, 0.3);
    }
    50% {
        box-shadow: 0 0 40px rgba(200, 7, 7, 0.5);
    }
`;

export const shimmer = keyframes`
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
`;

export const progressFill = keyframes`
    from { stroke-dashoffset: 283; }
`;

export const confettiDrop = keyframes`
    0% {
        transform: translateY(-100%) rotate(0deg);
        opacity: 1;
    }
    100% {
        transform: translateY(100vh) rotate(720deg);
        opacity: 0;
    }
`;

// ============================================
// LAYOUT COMPONENTS
// ============================================
export const PageContainer = styled.div`
    min-height: 100vh;
    padding: 40px 20px;
    background: linear-gradient(180deg,
        rgba(18, 18, 20, 1) 0%,
        rgba(25, 25, 30, 1) 50%,
        rgba(18, 18, 20, 1) 100%
    );
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 100%;
        max-width: 800px;
        height: 400px;
        background: radial-gradient(ellipse at center top,
            rgba(200, 7, 7, 0.15) 0%,
            transparent 70%
        );
        pointer-events: none;
    }
`;

export const ContentWrapper = styled.div`
    max-width: 900px;
    margin: 0 auto;
    position: relative;
    z-index: 1;
`;

export const HeroSection = styled.div`
    text-align: center;
    margin-bottom: 48px;
    animation: ${fadeInUp} 0.8s ease-out;
`;

export const Title = styled.h1`
    font-family: 'Work Sans', sans-serif;
    font-size: clamp(28px, 5vw, 42px);
    font-weight: 800;
    color: ${tokens.colors.text};
    margin: 0 0 8px 0;
    letter-spacing: -0.02em;
    text-transform: uppercase;
`;

export const Subtitle = styled.p`
    font-family: 'Work Sans', sans-serif;
    font-size: 14px;
    color: ${tokens.colors.textMuted};
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.15em;
`;

// ============================================
// SCORE DISPLAY
// ============================================
export const ScoreHero = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    margin-bottom: 48px;
    animation: ${scaleIn} 0.6s ease-out 0.2s both;
`;

export const ScoreCircle = styled.div`
    position: relative;
    width: 200px;
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;

    @media (max-width: 600px) {
        width: 160px;
        height: 160px;
    }
`;

export const ScoreRing = styled.svg`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transform: rotate(-90deg);
`;

export const ScoreRingBackground = styled.circle`
    fill: none;
    stroke: ${tokens.colors.surfaceLight};
    stroke-width: 8;
`;

export const ScoreRingProgress = styled.circle<{ $percent: number; $color?: string }>`
    fill: none;
    stroke: ${props => props.$color || tokens.colors.accent};
    stroke-width: 8;
    stroke-linecap: round;
    stroke-dasharray: 283;
    stroke-dashoffset: ${props => 283 - (283 * props.$percent) / 100};
    transition: stroke-dashoffset 1.5s cubic-bezier(0.4, 0, 0.2, 1);
    filter: drop-shadow(0 0 8px ${props => props.$color || tokens.colors.accent});
`;

export const ScoreValue = styled.div`
    text-align: center;
    z-index: 1;
`;

export const ScoreNumber = styled.div`
    font-family: 'JetBrains Mono', monospace;
    font-size: clamp(36px, 8vw, 52px);
    font-weight: 700;
    color: ${tokens.colors.text};
    line-height: 1;
    animation: ${countUp} 0.8s ease-out 0.4s both;
`;

export const ScoreLabel = styled.div`
    font-family: 'Work Sans', sans-serif;
    font-size: 12px;
    font-weight: 600;
    color: ${tokens.colors.textMuted};
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin-top: 4px;
`;

export const AccuracyBadge = styled.div<{ $percent: number }>`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: ${props => props.$percent >= 70
        ? `linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(22, 163, 74, 0.2) 100%)`
        : props.$percent >= 40
        ? `linear-gradient(135deg, rgba(251, 191, 36, 0.2) 0%, rgba(245, 158, 11, 0.2) 100%)`
        : `linear-gradient(135deg, rgba(200, 7, 7, 0.2) 0%, rgba(160, 6, 6, 0.2) 100%)`
    };
    border: 1px solid ${props => props.$percent >= 70
        ? 'rgba(34, 197, 94, 0.3)'
        : props.$percent >= 40
        ? 'rgba(251, 191, 36, 0.3)'
        : 'rgba(200, 7, 7, 0.3)'
    };
    border-radius: 30px;
    animation: ${fadeIn} 0.5s ease-out 0.8s both;
`;

export const AccuracyIcon = styled.span`
    font-size: 18px;
`;

export const AccuracyText = styled.span`
    font-family: 'Work Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: ${tokens.colors.text};
`;

// ============================================
// ROUNDS SECTION
// ============================================
export const SectionTitle = styled.h2`
    font-family: 'Work Sans', sans-serif;
    font-size: 13px;
    font-weight: 700;
    color: ${tokens.colors.textDim};
    text-transform: uppercase;
    letter-spacing: 0.15em;
    margin: 0 0 20px 0;
    display: flex;
    align-items: center;
    gap: 12px;

    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: linear-gradient(90deg, ${tokens.colors.border} 0%, transparent 100%);
    }
`;

export const RoundsGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 40px;
`;

export const RoundCard = styled.div<{ $index: number; $color: string }>`
    display: grid;
    grid-template-columns: auto 1fr auto auto;
    gap: 16px;
    align-items: center;
    padding: 16px 20px;
    background: ${tokens.colors.surface};
    backdrop-filter: blur(${tokens.blur});
    border: 1px solid ${tokens.colors.border};
    border-radius: ${tokens.radiusSmall};
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    animation: ${slideInLeft} 0.5s ease-out ${props => 0.1 + props.$index * 0.1}s both;

    &:hover {
        background: ${tokens.colors.surfaceLighter};
        border-color: ${props => props.$color}40;
        transform: translateX(4px);
        box-shadow: -4px 0 0 ${props => props.$color};
    }

    @media (max-width: 600px) {
        grid-template-columns: auto 1fr;
        gap: 12px;
    }
`;

export const RoundIndicator = styled.div<{ $color: string }>`
    width: 44px;
    height: 44px;
    background: linear-gradient(135deg, ${props => props.$color}30 0%, ${props => props.$color}10 100%);
    border: 2px solid ${props => props.$color};
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'JetBrains Mono', monospace;
    font-size: 16px;
    font-weight: 700;
    color: ${props => props.$color};
`;

export const RoundInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
`;

export const RoundTitle = styled.div`
    font-family: 'Work Sans', sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: ${tokens.colors.text};
`;

export const RoundLocation = styled.div`
    font-family: 'Work Sans', sans-serif;
    font-size: 12px;
    color: ${tokens.colors.textMuted};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const RoundDistance = styled.div<{ $missed?: boolean }>`
    font-family: 'JetBrains Mono', monospace;
    font-size: 14px;
    font-weight: 500;
    color: ${props => props.$missed ? tokens.colors.textDim : tokens.colors.textMuted};
    text-align: right;
    white-space: nowrap;

    @media (max-width: 600px) {
        grid-column: 2;
        text-align: left;
    }
`;

export const RoundScore = styled.div<{ $score: number }>`
    font-family: 'JetBrains Mono', monospace;
    font-size: 16px;
    font-weight: 700;
    color: ${props => props.$score >= 80
        ? tokens.colors.success
        : props.$score >= 50
        ? tokens.colors.gold
        : tokens.colors.text
    };
    min-width: 80px;
    text-align: right;

    @media (max-width: 600px) {
        grid-column: 2;
        text-align: left;
        min-width: auto;
    }
`;

// ============================================
// PODIUM (Battle Mode)
// ============================================
export const PodiumContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: flex-end;
    gap: 12px;
    margin-bottom: 48px;
    padding: 20px;
    animation: ${fadeInUp} 0.8s ease-out 0.3s both;

    @media (max-width: 600px) {
        gap: 8px;
        padding: 10px;
    }
`;

export const PodiumPlace = styled.div<{ $place: 1 | 2 | 3; $isMe?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: ${props => props.$place === 1 ? '140px' : '120px'};
    order: ${props => props.$place === 1 ? 2 : props.$place === 2 ? 1 : 3};

    @media (max-width: 600px) {
        width: ${props => props.$place === 1 ? '110px' : '90px'};
    }
`;

export const PodiumAvatar = styled.div<{ $place: 1 | 2 | 3; $isMe?: boolean }>`
    width: ${props => props.$place === 1 ? '80px' : '64px'};
    height: ${props => props.$place === 1 ? '80px' : '64px'};
    border-radius: 20px;
    background: ${props =>
        props.$place === 1 ? `linear-gradient(135deg, ${tokens.colors.gold} 0%, #f59e0b 100%)` :
        props.$place === 2 ? `linear-gradient(135deg, ${tokens.colors.silver} 0%, #64748b 100%)` :
        `linear-gradient(135deg, ${tokens.colors.bronze} 0%, #b45309 100%)`
    };
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${props => props.$place === 1 ? '32px' : '24px'};
    margin-bottom: 12px;
    box-shadow: 0 8px 24px ${props =>
        props.$place === 1 ? tokens.colors.goldGlow :
        props.$place === 2 ? tokens.colors.silverGlow :
        tokens.colors.bronzeGlow
    };
    position: relative;

    ${props => props.$isMe && css`
        &::after {
            content: '';
            position: absolute;
            inset: -4px;
            border-radius: 24px;
            border: 2px solid ${tokens.colors.accent};
            animation: ${pulseGlow} 2s ease-in-out infinite;
        }
    `}

    @media (max-width: 600px) {
        width: ${props => props.$place === 1 ? '64px' : '52px'};
        height: ${props => props.$place === 1 ? '64px' : '52px'};
        font-size: ${props => props.$place === 1 ? '24px' : '20px'};
    }
`;

export const PodiumName = styled.div<{ $isMe?: boolean }>`
    font-family: 'Work Sans', sans-serif;
    font-size: 14px;
    font-weight: ${props => props.$isMe ? '700' : '600'};
    color: ${props => props.$isMe ? tokens.colors.accent : tokens.colors.text};
    text-align: center;
    margin-bottom: 4px;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const PodiumScore = styled.div`
    font-family: 'JetBrains Mono', monospace;
    font-size: 13px;
    font-weight: 600;
    color: ${tokens.colors.textMuted};
    margin-bottom: 12px;
`;

export const PodiumBlock = styled.div<{ $place: 1 | 2 | 3 }>`
    width: 100%;
    height: ${props => props.$place === 1 ? '100px' : props.$place === 2 ? '70px' : '50px'};
    background: ${props =>
        props.$place === 1 ? `linear-gradient(180deg, ${tokens.colors.gold}40 0%, ${tokens.colors.gold}20 100%)` :
        props.$place === 2 ? `linear-gradient(180deg, ${tokens.colors.silver}30 0%, ${tokens.colors.silver}15 100%)` :
        `linear-gradient(180deg, ${tokens.colors.bronze}30 0%, ${tokens.colors.bronze}15 100%)`
    };
    border: 1px solid ${props =>
        props.$place === 1 ? `${tokens.colors.gold}50` :
        props.$place === 2 ? `${tokens.colors.silver}40` :
        `${tokens.colors.bronze}40`
    };
    border-radius: 12px 12px 0 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'JetBrains Mono', monospace;
    font-size: ${props => props.$place === 1 ? '32px' : '24px'};
    font-weight: 700;
    color: ${props =>
        props.$place === 1 ? tokens.colors.gold :
        props.$place === 2 ? tokens.colors.silver :
        tokens.colors.bronze
    };

    @media (max-width: 600px) {
        height: ${props => props.$place === 1 ? '80px' : props.$place === 2 ? '55px' : '40px'};
        font-size: ${props => props.$place === 1 ? '24px' : '20px'};
    }
`;

// ============================================
// PLAYER LIST (Battle Mode)
// ============================================
export const PlayersList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 40px;
`;

export const PlayerCard = styled.div<{ $isMe?: boolean; $isActive?: boolean; $rank: number }>`
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 16px;
    align-items: center;
    padding: 14px 18px;
    background: ${props => props.$isMe
        ? 'rgba(200, 7, 7, 0.15)'
        : props.$isActive
        ? tokens.colors.surfaceLighter
        : tokens.colors.surface
    };
    border: 1px solid ${props => props.$isMe
        ? 'rgba(200, 7, 7, 0.3)'
        : props.$isActive
        ? tokens.colors.borderLight
        : tokens.colors.border
    };
    border-radius: ${tokens.radiusSmall};
    cursor: pointer;
    transition: all 0.25s ease;
    animation: ${fadeInUp} 0.5s ease-out ${props => 0.1 + props.$rank * 0.05}s both;

    &:hover {
        background: ${props => props.$isMe
            ? 'rgba(200, 7, 7, 0.2)'
            : tokens.colors.surfaceLighter
        };
        transform: translateX(4px);
    }
`;

export const PlayerRank = styled.div<{ $rank: number }>`
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: ${props =>
        props.$rank === 1 ? `linear-gradient(135deg, ${tokens.colors.gold} 0%, #f59e0b 100%)` :
        props.$rank === 2 ? `linear-gradient(135deg, ${tokens.colors.silver} 0%, #64748b 100%)` :
        props.$rank === 3 ? `linear-gradient(135deg, ${tokens.colors.bronze} 0%, #b45309 100%)` :
        tokens.colors.surfaceLight
    };
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'JetBrains Mono', monospace;
    font-size: 14px;
    font-weight: 700;
    color: ${props => props.$rank <= 3 ? '#000' : tokens.colors.textMuted};
`;

export const PlayerInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

export const PlayerName = styled.div<{ $isMe?: boolean }>`
    font-family: 'Work Sans', sans-serif;
    font-size: 15px;
    font-weight: ${props => props.$isMe ? '700' : '600'};
    color: ${props => props.$isMe ? tokens.colors.accent : tokens.colors.text};
`;

export const PlayerSubtext = styled.div`
    font-family: 'Work Sans', sans-serif;
    font-size: 12px;
    color: ${tokens.colors.textMuted};
`;

export const PlayerScore = styled.div`
    font-family: 'JetBrains Mono', monospace;
    font-size: 18px;
    font-weight: 700;
    color: ${tokens.colors.text};
`;

// ============================================
// BUTTONS & ACTIONS
// ============================================
export const ActionsSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    margin: 40px 0;
    animation: ${fadeIn} 0.5s ease-out 1s both;
`;

export const PrimaryButton = styled(Link)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 16px 32px;
    background: linear-gradient(135deg, ${tokens.colors.accent} 0%, #a00606 100%);
    border: none;
    border-radius: ${tokens.radiusSmall};
    font-family: 'Work Sans', sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: ${tokens.colors.text};
    text-decoration: none;
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 16px ${tokens.colors.accentGlow};

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px ${tokens.colors.accentGlow};
        color: ${tokens.colors.text};
    }

    &:active {
        transform: translateY(0);
    }
`;

export const SecondaryButton = styled(Link)`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: transparent;
    border: 1px solid ${tokens.colors.border};
    border-radius: ${tokens.radiusSmall};
    font-family: 'Work Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: ${tokens.colors.textMuted};
    text-decoration: none;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: ${tokens.colors.surfaceLight};
        border-color: ${tokens.colors.borderLight};
        color: ${tokens.colors.text};
    }
`;

// ============================================
// MAP SECTION
// ============================================
export const MapSection = styled.div`
    margin-top: 48px;
    border-radius: ${tokens.radiusLarge};
    overflow: hidden;
    border: 1px solid ${tokens.colors.border};
    animation: ${fadeInUp} 0.8s ease-out 1.2s both;

    /* Make map take full width and have good height */
    #smap-container {
        width: 100% !important;
        max-width: 100% !important;
    }

    #smap-container,
    .smap-result {
        width: 100% !important;
        min-height: 500px !important;
        height: 60vh !important;
        max-height: 700px !important;
    }

    @media (max-width: 768px) {
        #smap-container,
        .smap-result {
            min-height: 350px !important;
            height: 50vh !important;
        }
    }
`;

export const MapHeader = styled.div`
    padding: 16px 20px;
    background: ${tokens.colors.surface};
    border-bottom: 1px solid ${tokens.colors.border};
    display: flex;
    align-items: center;
    gap: 12px;
`;

export const MapIcon = styled.span`
    width: 32px;
    height: 32px;
    background: ${tokens.colors.surfaceLight};
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
`;

export const MapTitle = styled.h3`
    font-family: 'Work Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: ${tokens.colors.text};
    margin: 0;
`;

// ============================================
// DONATE SECTION WRAPPER
// ============================================
export const DonateWrapper = styled.div`
    margin: 40px 0;
    padding: 24px;
    background: ${tokens.colors.surface};
    border: 1px solid ${tokens.colors.border};
    border-radius: ${tokens.radius};
    animation: ${fadeIn} 0.5s ease-out 1.4s both;
`;

// ============================================
// CONFETTI (for high scores)
// ============================================
export const ConfettiPiece = styled.div<{ $delay: number; $x: number; $color: string }>`
    position: fixed;
    top: -20px;
    left: ${props => props.$x}%;
    width: 10px;
    height: 10px;
    background: ${props => props.$color};
    border-radius: 2px;
    animation: ${confettiDrop} 3s ease-in ${props => props.$delay}s forwards;
    pointer-events: none;
    z-index: 1000;
`;
